/**
*****************************************
*										*
* NAGARRO  - CSX - CONFIDENTIAL         *
*										*
*****************************************
*/
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';

import { AppUtilityService } from '../../../core/services/app-utility.service';
import { UntypedFormGroup } from '@angular/forms';
import { LmsLoaderService } from '../../../core/services/lms-loader.service';
import { LocoDetail } from '../../../core/model/loco-detail.model';
import { LocomotiveErrors } from '../../../core/model/locomotive-error.model';
import { SelectItem } from 'primeng/api/selectitem';
import { StaticDataStoreService } from '../../../core/services/static-datastore.service';
import { Tab } from '../../../core/model/tab.model';
import { TrainData } from '../../../core/model/train-data.model';
import { TrainEditOps } from '../../shared/model/train-edit-operation.model';
import { UserService } from '../../../core/services/user.service';

/**
 * @export
 * @class LmsTrainLocoDetailsDataComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-train-loco-details-data',
  templateUrl: './lms-train-loco-details-data.component.html',
  styleUrls: ['./lms-train-loco-details-data.component.scss']
})
export class LmsTrainLocoDetailsDataComponent implements OnInit, OnDestroy, OnChanges {

  @Input() loco: LocoDetail;
  @Input() id: number;
  @Input() selectedTab: Tab;
  @Input() trainEdit: TrainEditOps;
  @Input() topToBottom: string;
  @Input() group: any;
  @Input() formSubmitted: boolean;
  @Input() locoUpdateErrors: Map<string, LocomotiveErrors>;
  @Input() tabIndexes: number;
  plannedAssignmentValue: string;

  locoError: LocomotiveErrors = {};
  locoForm: UntypedFormGroup;
  isEditable = false;
  maxDateValue: Date = new Date();
  minDateValue: Date = new Date();
  isLastElement = false;
  returnAction: string;
  directionDropdown: SelectItem[];
  deadIsolatedDropdown: SelectItem[];

  @Input() trainData: TrainData;

  @Output() onLocoSelect: EventEmitter<LocoDetail> = new EventEmitter<LocoDetail>();

  @Output() editAction: EventEmitter<TrainEditOps> = new EventEmitter<TrainEditOps>();

  @Output() actionTrigger: EventEmitter<string> = new EventEmitter<string>();

  trainLocoIntendedUsageCode?: string;
  trainLocoIntendedUsageValue?: string;
  trainLocoIntendedUsageReason?: string;
  trainLocointendedUsageNote?: string;

  /**
   * Creates an instance of LmsTrainLocoDetailsDataComponent.
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  constructor(private lmsLoaderService: LmsLoaderService,
    private staticDataStoreService: StaticDataStoreService,
    public appUtilService: AppUtilityService, 
    public user: UserService) {
    this.returnAction = '';
    this.directionDropdown = [];
    this.directionDropdown.push({
      label: 'Forward',
      value: 'F'
    });
    this.directionDropdown.push({
      label: 'Backward',
      value: 'B',
    });
    this.directionDropdown.push({
      label: 'Unknown',
      value: ' '
    });

    this.deadIsolatedDropdown = [];
    this.deadIsolatedDropdown.push({
      label: 'Dead',
      value: 'F'
    });
    this.deadIsolatedDropdown.push({
      label: 'Running Isolated',
      value: 'Z',
    });
    this.deadIsolatedDropdown.push({
      label: 'Unknown',
      value: ' '
    });
    this.plannedAssignmentValue = '';
  }

  /**
   * Initialize the state of lifecycle hook.
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  ngOnInit() {
    this.locoError.milesError = '';
    this.locoError.directionFacingError = '';
    this.locoError.fuelStationError = '';
    this.locoError.fuelMMDDError = '';
    this.locoError.deadIsolatedError = '';
    if (this.loco) {
      this.trainLocoIntendedUsageCode = this.loco.intendedUsageCode;
      this.trainLocoIntendedUsageValue = this.getIntendedUsageCode(this.loco.intendedUsageCode);
      this.trainLocoIntendedUsageReason = this.appUtilService.getIntendedUsageReasonDesc(this.loco.intendedUsageReason);
      this.trainLocointendedUsageNote = this.loco.intendedUsageNote;
    }
  }

  /**
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  ngOnChanges() {
    if (this.loco) {
      this.trainLocoIntendedUsageCode = this.loco.intendedUsageCode;
      this.trainLocoIntendedUsageValue = this.getIntendedUsageCode(this.loco.intendedUsageCode);
      this.trainLocoIntendedUsageReason = this.appUtilService.getIntendedUsageReasonDesc(this.loco.intendedUsageReason);
      this.trainLocointendedUsageNote = this.loco.intendedUsageNote;
    }
    if (this.trainEdit.locoEditMode && !this.formSubmitted) {
      if (this.group) {
        for (let i = 0; i < this.trainEdit.selectedLocomotives.length; i++) {
          if (this.group[i].value.locoID === this.loco.locomotiveIdentifier) {
            if (i === 0) {
              setTimeout(() => {
                const firstElem = document.getElementById('firstElem' + this.group[0].value.locoID);
                if (firstElem) {
                  firstElem.focus();
                }
              }, 30);
            }
            if (i === this.trainEdit.selectedLocomotives.length - 1) {
              this.isLastElement = true;
              setTimeout(() => {
                this.lmsLoaderService.hideLoader();
              }, 100);
            }
            this.locoForm = this.group[i];
            this.isEditable = true;
            this.minDateValue = this.locoForm.value.minDate;
            break;
          }
        }
      }
    } else if (this.trainEdit.locoEditMode && this.locoUpdateErrors && this.locoUpdateErrors.size > 0 && this.isEditable) {
      this.locoError = this.locoUpdateErrors.get(this.loco.locomotiveIdentifier);
      if (!this.locoError) {
        this.locoError = {};
        this.locoError.milesError = '';
        this.locoError.directionFacingError = '';
        this.locoError.fuelStationError = '';
        this.locoError.fuelMMDDError = '';
        this.locoError.deadIsolatedError = '';
      }
    }
    if (!this.plannedAssignmentValue || this.plannedAssignmentValue.length < 1) {
      if (this.loco && this.appUtilService.isPlannedLoco(this.loco)) {
        const planValue = this.getPlannedAssignmentLocation();
        if (planValue === 'TRAIN') {
          this.plannedAssignmentValue = this.loco.plannedOrCurrentAssign;
          if (this.loco.plannedOrCurrentLoc) {
            this.plannedAssignmentValue += ' at ' + this.loco.plannedOrCurrentLoc;
          }
        } else if (planValue === 'FOREIGNROAD') {
          if (this.loco.plannedOrCurrentLoc) {
            this.plannedAssignmentValue += 'To ' + this.loco.plannedOrCurrentLoc;
          }
          if (this.loco.plannedOrCurrentAssign) {
            this.plannedAssignmentValue += ' at ' + this.loco.plannedOrCurrentAssign;
          }
        } else if (planValue === 'TERMINAL') {
          this.plannedAssignmentValue = this.loco.plannedOrCurrentAssign;
        }
        if (!this.plannedAssignmentValue || this.plannedAssignmentValue.trim().length < 1) {
          this.plannedAssignmentValue = '-';
        }
      }
    };
  }

  /**
   * @private
   * @returns {string}
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  private getPlannedAssignmentLocation(): string {
    let returnValue = '';
    if (this.appUtilService.isValidTrainId(this.loco.plannedOrCurrentAssign)) {
      returnValue = 'TRAIN';
    } else if (this.appUtilService.isMnemonicExist(this.loco.plannedOrCurrentAssign)) {
      if (this.loco.plannedOrCurrentLoc) {
        returnValue = 'FOREIGNROAD';
      } else {
        returnValue = 'TERMINAL';
      }
    }

    return returnValue;
  }

  /**
   * This mehtod handle the selection of locomotive
   * to perform various operation mentioned in otherAction button
   * @param {any} event
   * @param {LocoDetail} locomotive
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  public locomotiveSelection(event, locomotive: LocoDetail) {
    if (this.trainEdit.locoSelection && !this.trainEdit.dragMode && this.user && this.user.isUserManagerOrDirector) {
      const element = document.getElementById('selection' + locomotive.locomotiveIdentifier);
      let currentClass;
      if (element) {
        currentClass = element.getAttribute('class');
      }
      if ('locomotiveSelection' === currentClass) {
        element.removeAttribute('class');
        if (element.children[0] && element.children[0].children[0]) {
          element.children[0].children[0].classList.remove('selectedDraged');
        }
        this.removeLocomotiveSelection(locomotive);
      } else {
        this.trainEdit.selectedLocomotives.push(locomotive);
      }
      this.controlLocomotiveSelection(false);
      this.editAction.emit(this.trainEdit);
      this.actionTrigger.emit(this.getBottomUpAction());
    }
  }

  /**
   * This method is used to handle events
   * for communication between multiple components
   * @private
   * @returns {string}
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  private getBottomUpAction(): string {
    let returnValue = this.returnAction + 'selection';
    this.trainEdit.selectedLocomotives.forEach(locomotive => {
      returnValue += locomotive.locomotiveIdentifier + '';
    });
    this.returnAction = returnValue;
    return returnValue;
  }

  /**
   * This method is used to reset the train operation
   * operation like mentioned in otherAction button
   * @private
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  private resetTrainOperations() {
    this.trainEdit.draggable = false;
    this.trainEdit.selectedLocomotives = [];
    this.trainEdit.locoSelection = false;
    this.trainEdit.locoEditMode = false;
    this.trainEdit.submitAction = false;
  }

  /**
   * This mehtod is helper method for
   * locomotiveSelection() mehtod
   * This method is used to check whether
   * newly clicked locomotive is to be removed from
   * selections or add to selections.
   * @private
   * @param {boolean} isRemove
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  private controlLocomotiveSelection(isRemove: boolean) {
    if (!isRemove && this.trainEdit.selectedLocomotives && this.trainEdit.selectedLocomotives.length > 0) {
      this.trainEdit.selectedLocomotives.forEach(element => {
        const elem = document.getElementById('selection' + element.locomotiveIdentifier);
        if (elem) {
          elem.setAttribute('class', 'locomotiveSelection');
          if (elem.children[0] && elem.children[0].children[0]) {
            elem.children[0].children[0].classList.add('selectedDraged');
          }
        }
      });
    } else if (isRemove && this.trainEdit.selectedLocomotives && this.trainEdit.selectedLocomotives.length > 0) {
      this.trainEdit.selectedLocomotives.forEach(element => {
        const elem = document.getElementById('selection' + element.locomotiveIdentifier);
        if (elem) {
          elem.removeAttribute('class');
          if (elem.children[0] && elem.children[0].children[0] && elem.children[0].children[0].classList.length > 0) {
            if (elem.children[0].children[0].classList.contains('selectedDraged')) {
              elem.children[0].children[0].classList.remove('selectedDraged');
            }
          }
        }
      });
      this.trainEdit.selectedLocomotives = [];
    }
  }

  /**
   * This method is helper mehtod for controlLocomotiveSelection() method
   * @private
   * @param {LocoDetail} locomotive
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  private removeLocomotiveSelection(locomotive: LocoDetail) {
    this.trainEdit.selectedLocomotives = this.trainEdit.selectedLocomotives.filter((element) => {
      return locomotive.locomotiveIdentifier !== element.locomotiveIdentifier;
    });
  }

  /**
   * clean the state of lifecycle hook.
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  ngOnDestroy() {
  }

  /**
   * This method shows lococmotive COntext menu.
   * @param {LocoDetail} loco
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  public showLocoContextMenu(loco: LocoDetail) {
    if (loco && loco.locomotiveIdentifier && loco.locomotiveIdentifier.length > 5) {
      this.onLocoSelect.emit(loco);
    }
  }

  /**
   * this mehtod is used to handle the tab order in application.
   * if this is the last element in editable grid then this method
   * will set focus to user profile btn
   * @memberOf LmsTrainLocoDetailsDataComponent
   */
  setFocusToUserProfile() {
    if (this.isLastElement) {
      const userProfile = document.getElementById('userProfileIcon');
      if (userProfile) {
        window.scroll(0, 0);
        setTimeout(() => {
          userProfile.focus();
        }, 3);
      }
    }
  }

  getIntendedUsageCode(code: string) {
    let codeValue = '-';
    if (code) {
      codeValue = this.appUtilService.getIntendedUsageDesc(code);
    }
    return codeValue;
  }
}
